import React, { useEffect, useState } from 'react';
import {
  Modal,
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Text,
  Badge,
  Divider,
  Icon,
  Select,
  Spinner,
} from '@shopify/polaris';
import {
  PersonIcon,
  LocationIcon,
  CalendarIcon,
  PackageIcon,
  CashDollarIcon,
  NoteIcon,
} from '@shopify/polaris-icons';
import { RentalItemThumbnails } from './RentalOverviewCard';
import RentalFulfillmentControl from './RentalFulfillmentControl';
import { rentalsAPI } from '../services/api';

const CONDITION_LABELS = {
  new: 'New',
  very_good: 'Very good',
  good: 'Good',
  fair: 'Fair',
};

function selectionsFromPacking(packing) {
  const selections = {};
  for (const product of packing?.products || []) {
    const assigned = product.physicalItems
      .filter((item) => item.assignedToThisRental)
      .map((item) => String(item.id));
    selections[String(product.productId)] = Array.from(
      { length: product.requiredQuantity },
      (_, index) => assigned[index] || ''
    );
  }
  return selections;
}

const RentalDetailModal = ({ rental: initialRental, open, onClose, onUpdated }) => {
  const [rental, setRental] = useState(initialRental);
  useEffect(() => { setRental(initialRental); }, [initialRental]);
  const [packing, setPacking] = useState(null);
  const [selections, setSelections] = useState({});
  const [packingLoading, setPackingLoading] = useState(false);
  const [packingSaving, setPackingSaving] = useState(false);
  const [packingError, setPackingError] = useState('');
  const [packingSaved, setPackingSaved] = useState(false);
  const [packingDirty, setPackingDirty] = useState(false);

  useEffect(() => {
    if (!open || !rental?.id) return;
    let active = true;
    setPackingLoading(true);
    setPacking(null);
    setSelections({});
    setPackingError('');
    setPackingSaved(false);
    setPackingDirty(false);
    rentalsAPI.getPacking(rental.id)
      .then((response) => {
        if (!active) return;
        setPacking(response.data);
        setSelections(selectionsFromPacking(response.data));
      })
      .catch((error) => {
        if (!active) return;
        setPacking(null);
        setPackingError(error.response?.data?.error || 'Failed to load physical items');
      })
      .finally(() => {
        if (active) setPackingLoading(false);
      });
    return () => { active = false; };
  }, [open, rental?.id]);

  if (!rental) return null;

  const updateSelection = (productId, slotIndex, itemId) => {
    setSelections((current) => {
      const nextProduct = [...(current[String(productId)] || [])];
      nextProduct[slotIndex] = itemId;
      return { ...current, [String(productId)]: nextProduct };
    });
    setPackingDirty(true);
    setPackingSaved(false);
  };

  const savePacking = async () => {
    const assignments = Object.entries(selections).flatMap(([productId, itemIds]) =>
      itemIds.filter(Boolean).map((itemId) => ({
        productId: Number(productId),
        itemId: Number(itemId),
      }))
    );
    try {
      setPackingSaving(true);
      setPackingError('');
      setPackingSaved(false);
      const response = await rentalsAPI.savePacking(rental.id, assignments);
      setPacking(response.data);
      setSelections(selectionsFromPacking(response.data));
      setPackingDirty(false);
      setPackingSaved(true);
    } catch (error) {
      setPackingError(error.response?.data?.error || 'Failed to save packed items');
    } finally {
      setPackingSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    return dateStr
      ? new Date(dateStr).toLocaleDateString('de-DE', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : '—';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(Number(amount) || 0);
  };

  const customerName = [rental.customerFirstName, rental.customerLastName]
    .filter(Boolean)
    .join(' ') || rental.customerEmail || '—';

  const addressParts = [
    rental.address1,
    rental.address2,
    [rental.zip, rental.city].filter(Boolean).join(' '),
    rental.province,
    rental.country,
  ].filter(Boolean);
  const address = addressParts.length > 0 ? addressParts.join(', ') : 'No address provided';

  const getStatusBadge = (status) => {
    const config = {
      draft: { tone: 'info', children: 'Draft' },
      pending: { tone: 'attention', children: 'Pending' },
      confirmed: { tone: 'info', children: 'Confirmed' },
      active: { tone: 'success', children: 'Active' },
      overdue: { tone: 'critical', children: 'Overdue' },
      returned: { tone: 'success', children: 'Returned' },
      cancelled: { tone: 'warning', children: 'Cancelled' },
    };
    return <Badge {...(config[status] || { children: status })} />;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Rental #${rental.id}`}
      size="large"
    >
      <Modal.Section>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd" as="h2">Rental #{rental.shopifyOrderId || rental.id}</Text>
            {getStatusBadge(rental.status)}
          </InlineStack>

          <RentalFulfillmentControl rental={rental} onUpdated={(updated) => {
            setRental(updated);
            onUpdated?.(updated);
          }} />

          <Divider />

          <InlineStack gap="300" blockAlign="start">
            <div style={{ marginTop: 2 }}>
              <Icon source={PersonIcon} />
            </div>
            <BlockStack gap="100">
              <Text variant="headingSm" as="h3">Customer</Text>
              <Text variant="bodyMd">{customerName}</Text>
              {rental.customerEmail && (
                <Text variant="bodySm" tone="subdued">{rental.customerEmail}</Text>
              )}
              {rental.customerPhone && (
                <Text variant="bodySm" tone="subdued">{rental.customerPhone}</Text>
              )}
            </BlockStack>
          </InlineStack>

          <Divider />

          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="headingSm" as="h3">Packed physical items</Text>
              {packing && (
                <Badge tone={packing.products.every((product) =>
                  (selections[String(product.productId)] || []).filter(Boolean).length ===
                    product.requiredQuantity
                ) ? 'success' : 'attention'}>
                  {Object.values(selections).reduce(
                    (sum, itemIds) => sum + itemIds.filter(Boolean).length,
                    0
                  )} / {' '}
                  {packing.products.reduce((sum, product) => sum + product.requiredQuantity, 0)} packed
                </Badge>
              )}
            </InlineStack>

            {packingLoading && (
              <InlineStack gap="200" blockAlign="center">
                <Spinner size="small" />
                <Text tone="subdued">Loading physical items...</Text>
              </InlineStack>
            )}
            {packingError && <Banner tone="critical">{packingError}</Banner>}
            {packingSaved && <Banner tone="success">Packed items saved.</Banner>}

            {!packingLoading && packing?.products.map((product) => {
              const productSelections = selections[String(product.productId)] || [];
              const selectedElsewhere = new Set(productSelections.filter(Boolean));
              return (
                <Card key={product.productId}>
                  <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                      <BlockStack gap="050">
                        <Text variant="headingSm">{product.title}</Text>
                        {product.parentBundleTitle && (
                          <Text variant="bodySm" tone="subdued">
                            Part of {product.parentBundleTitle}
                          </Text>
                        )}
                      </BlockStack>
                      <Badge tone={productSelections.filter(Boolean).length === product.requiredQuantity ? 'success' : 'attention'}>
                        {productSelections.filter(Boolean).length} / {product.requiredQuantity}
                      </Badge>
                    </InlineStack>

                    {product.trackedQuantity === 0 && (
                      <Banner tone="warning">
                        No physical items are registered for this product. Add them in Conditions first.
                      </Banner>
                    )}

                    {Array.from({ length: product.requiredQuantity }, (_, slotIndex) => {
                      const selectedValue = productSelections[slotIndex] || '';
                      const options = [
                        { label: 'Not packed yet', value: '' },
                        ...product.physicalItems.map((item) => {
                          const identity = item.barcode ? item.barcode : `Item #${item.id}`;
                          const condition = CONDITION_LABELS[item.condition] || item.condition;
                          const conflict = item.status === 'unavailable' ? ' — Unavailable'
                            : !item.available ? ` — rental #${item.conflictingRentalId}` : '';
                          return {
                            label: `${identity} — ${condition}${conflict}`,
                            value: String(item.id),
                            disabled: !item.available || (
                              selectedElsewhere.has(String(item.id)) && selectedValue !== String(item.id)
                            ),
                          };
                        }),
                      ];
                      return (
                        <Select
                          key={`${product.productId}-${slotIndex}`}
                          label={`${product.title}: unit ${slotIndex + 1}`}
                          options={options}
                          value={selectedValue}
                          disabled={!packing.editable || packingSaving}
                          onChange={(value) => updateSelection(product.productId, slotIndex, value)}
                        />
                      );
                    })}
                  </BlockStack>
                </Card>
              );
            })}

            {!packingLoading && packing && !packing.editable && (
              <Text variant="bodySm" tone="subdued">
                Physical items cannot be assigned to draft or cancelled rentals.
              </Text>
            )}
            {!packingLoading && packing?.editable && (
              <InlineStack align="end">
                <Button
                  variant="primary"
                  loading={packingSaving}
                  disabled={!packingDirty || packingSaving}
                  onClick={savePacking}
                >
                  Save packed items
                </Button>
              </InlineStack>
            )}
          </BlockStack>

          <InlineStack gap="300" blockAlign="start">
            <div style={{ marginTop: 2 }}>
              <Icon source={LocationIcon} />
            </div>
            <BlockStack gap="100">
              <Text variant="headingSm" as="h3">Shipping address</Text>
              <Text variant="bodyMd">{address}</Text>
            </BlockStack>
          </InlineStack>

          <InlineStack gap="300" blockAlign="start">
            <div style={{ marginTop: 2 }}>
              <Icon source={CalendarIcon} />
            </div>
            <BlockStack gap="100">
              <Text variant="headingSm" as="h3">Rental period</Text>
              <Text variant="bodyMd">
                {formatDate(rental.startDate)} – {formatDate(rental.endDate)}
              </Text>
            </BlockStack>
          </InlineStack>

          <InlineStack gap="300" blockAlign="start">
            <div style={{ marginTop: 2 }}>
              <Icon source={PackageIcon} />
            </div>
            <BlockStack gap="100">
              <Text variant="headingSm" as="h3">Items</Text>
              <RentalItemThumbnails items={rental.rentalItems} textVariant="bodyMd" />
            </BlockStack>
          </InlineStack>

          <InlineStack gap="300" blockAlign="start">
            <div style={{ marginTop: 2 }}>
              <Icon source={CashDollarIcon} />
            </div>
            <BlockStack gap="100">
              <Text variant="headingSm" as="h3">Pricing</Text>
              <BlockStack gap="050">
                <Text variant="bodyMd">Subtotal: {formatCurrency(rental.subtotal)}</Text>
                {Number(rental.securityDeposit) > 0 && (
                  <Text variant="bodyMd">Security deposit: {formatCurrency(rental.securityDeposit)}</Text>
                )}
                <Text variant="bodyMd" fontWeight="semibold">Total: {formatCurrency(rental.total)}</Text>
              </BlockStack>
            </BlockStack>
          </InlineStack>

          {rental.notes && (
            <InlineStack gap="300" blockAlign="start">
              <div style={{ marginTop: 2 }}>
                <Icon source={NoteIcon} />
              </div>
              <BlockStack gap="100">
                <Text variant="headingSm" as="h3">Notes</Text>
                <Text variant="bodyMd">{rental.notes}</Text>
              </BlockStack>
            </InlineStack>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default RentalDetailModal;
